import React, { useEffect, useMemo, useState } from 'react';
import * as mobxReact from 'mobx-react';
import * as IoC from 'typescript-ioc';
import './date-time-filter.scss';
import { ReportConfigurationViewModel } from '../../../_reports/report-configuration/report-configuration.viewmodel';
import { IReportOperator, IReportOperatorItem } from '../../../../../../api/reports.dao';
import { FormField } from '../../../../../global/form/form-field/form-field';
import { DefaultDatePicker } from '../../../../../global/default-datepicker/default-datepicker';
import { TimeInput } from '../../../../../global/time-input/time-input';
import { injectIntl, IntlShape } from 'react-intl';
import { getFormattedTimezoneOffsetByTimezone } from '../../../../../../infrastructure/utils/date-fns-overrides.utils';
import { IGlobalStorage } from '../../../../../../infrastructure/global-storage';
import { DateFnsFormats, IDateTimeFormatter, TimeFormats } from '../../../../../../infrastructure/date-formatter';
import { IApiDateConverter } from '../../../../../../infrastructure/api-date-converter';

interface IProps {
    intl: IntlShape;
    field: string;
    ContentHeader: JSX.Element;
    configurationVm: ReportConfigurationViewModel;
}

const DateTimeFilter = injectIntl(mobxReact.observer((props: IProps) => {
    const {
        intl,
        field,
        ContentHeader,
        configurationVm,
    } = props;
    const globalStorage: IGlobalStorage = IoC.Container.get(IGlobalStorage);
    const dateTimeFormatter = IoC.Container.get(IDateTimeFormatter);
    const apiDateConverter = IoC.Container.get(IApiDateConverter);

    const operators: IReportOperatorItem[] = [{
        label: 'Before',
        value: 'BEFORE'
    }, {
        label: 'After',
        value: 'AFTER'
    }];

    const { applyFilter, clearFilter, getAppliedFilter } = configurationVm;
    const [operatorValue, setOperatorValue] = useState(operators[0].value);
    const appliedFilter = getAppliedFilter(field);

    const filterDate = apiDateConverter.apiStringToDate(appliedFilter?.values[0]);
    let selectedDate: Date;
    if (!isNaN(filterDate.getTime())) selectedDate = filterDate;

    const filterCommonProps = {
        field: field,
        customField: false,
        operator: operatorValue,
    };

    const handleChangeDate = (date?: Date) => {
        if (appliedFilter && !date) {
            clearFilter(field);
        } else if (date) {
            applyFilter({...filterCommonProps, values: [apiDateConverter.dateToAPIString(date)]});
        }
    };

    const handleChangeTime = (time: Date) => {
        const date = selectedDate || new Date();

        date.setHours(time.getHours(), time.getMinutes());
        applyFilter({...filterCommonProps, values: [apiDateConverter.dateToAPIString(date)]});
    };

    const handleChangeOperator = (value: IReportOperator) => {
        setOperatorValue(value);
        if (appliedFilter) applyFilter({...filterCommonProps, operator: value, values: appliedFilter.values});
    };

    const dateFormat =
        dateTimeFormatter.getLocalTimeFormat() === TimeFormats.Day24Hours
            ? DateFnsFormats.StringifiedShortFullDateDay24Hours
            : DateFnsFormats.StringifiedShortFullDateDay12Hours;

    const timeZone = useMemo(() => globalStorage.getEvent().timeZone, [globalStorage.getEvent()]);
    const formattedTimezoneOffset = useMemo(() => {
        return getFormattedTimezoneOffsetByTimezone(timeZone);
    }, [timeZone]);

    useEffect(() => {
        if (appliedFilter) setOperatorValue(appliedFilter.operator);
    }, [field]);

    return (
        <div className='date-time-filter'>
            <div className='date-time-filter__header'>
                {ContentHeader}
                <div className='date-time-filter__header-content'>
                    <select
                        className='date-time-filter__operator-chooser oe-form-field oe-form-field__control'
                        value={operatorValue}
                        onChange={e => handleChangeOperator(e.target.value as IReportOperator)}
                    >
                        { operators.map(operator =>
                            <option key={operator.value} value={operator.value}>{operator.label}</option>
                        )}
                    </select>
                    <div className="date-time-filter__fields">
                        <FormField
                            label={intl.formatMessage({
                                id: 'oe.meeting.date',
                                defaultMessage: 'Date',
                                description: 'Label text',
                            })}
                            className="date-time-filter__date-form-field"
                        >
                            <DefaultDatePicker
                                className="oe-form-field__control"
                                dateformat={dateFormat}
                                showTimeSelect={false}
                                selected={selectedDate}
                                onChange={handleChangeDate}
                                timeZone={timeZone}
                            />
                        </FormField>
                        <FormField
                            label={intl.formatMessage({
                                id: 'oe.meeting.time',
                                defaultMessage: 'Time',
                                description: 'Label text',
                            })}
                            className="date-time-filter__time-form-field"
                        >
                            <div className='date-time-filter__time'>
                                <TimeInput
                                    className="oe-form-field__control"
                                    date={selectedDate}
                                    onChange={handleChangeTime}
                                    timeZone={timeZone}
                                />
                                <div className='date-time-filter__timezone'>
                                    {formattedTimezoneOffset}
                                </div>
                            </div>
                        </FormField>
                    </div>
                </div>
            </div>
        </div>
    );
}));

export default DateTimeFilter;
